import { RepeatDataInput, RepeatStep } from '@/types/automationSteps.ts';
import { Group, Stack, Text } from '@mantine/core';
import * as React from 'react';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import SingleChildDataInputSelector from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleChildDataInputSelector.tsx';
import SingleDataInputSelectorInner from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleDataInputSelectorInner.tsx';
import { DEFAULT_DYNAMIC_DATA_INPUT, STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import StepFieldsRemoveButton from '@/pages/Editor/components/StepFieldsRemoveButton.tsx';
import OutputPinButton from '@/pages/Editor/components/StepSidebar/OutputPinButton.tsx';
import {
  REPEAT_ALL_OUTPUT_SUFFIX,
  REPEAT_LATEST_OUTPUT_SUFFIX,
} from '@/utils/pinnedOutputs.ts';

type props = {
  condition: RepeatDataInput;
  onChangeNoDelay: (updatedInput: RepeatDataInput) => void;
  onChangeDelay: (updatedInput: RepeatDataInput) => void;
  onDelete: () => void;
  step: RepeatStep;
  inputIndex: number;
};

const RepeatDataInputFields = ({
  condition,
  onChangeNoDelay,
  onChangeDelay,
  onDelete,
  step,
  inputIndex,
}: props) => {
  const invalidDataInput = useStepSpecificError(step, StepErrorType.InvalidDataInput, inputIndex);
  const invalidChildOutput = useStepSpecificError(
    step,
    StepErrorType.InvalidChildOutput,
    inputIndex,
  );

  const isNewStep = useIsNewStep(step.step_id);

  return (
    <Stack
      style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }}
      bg={'#F6F6F6'}
      p={'xs'}
      gap={'xs'}
    >
      <Stack>
        <Group gap={'xs'} wrap={'nowrap'} align={'start'}>
          <StyledTextInput
            label="Name"
            value={condition.name}
            placeholder="Input Name"
            onChange={(event) => {
              const value = event.currentTarget.value.replace(/[/\n]/g, '');
              onChangeDelay({ ...condition, name: value });
            }}
            required={true}
            maxLength={STEP_FIELD_MAX_LENGTH}
            w={'100%'}
            error={!condition.name && !isNewStep ? 'Name cannot be empty' : undefined}
          />
          <StepFieldsRemoveButton onDelete={onDelete} />
        </Group>
      </Stack>

      <SingleDataInputSelectorInner
        step={step}
        dataInputError={invalidDataInput}
        dataInput={condition.data_input}
        onChange={(dataInput) => onChangeNoDelay({ ...condition, data_input: dataInput })}
        includeEmptyDatasets={true}
      />

      <SingleChildDataInputSelector
        step={step}
        label={'Child Output'}
        dataInput={condition.child_data_output}
        onChange={(dataInput) =>
          onChangeNoDelay({
            ...condition,
            child_data_output: dataInput || DEFAULT_DYNAMIC_DATA_INPUT,
          })
        }
        dataInputError={invalidChildOutput}
        required={true}
      />

      <Group gap={12}>
        <Text size="xs" c="dimmed">
          Pin outputs
        </Text>
        <Group gap={6} wrap={'nowrap'}>
          <OutputPinButton
            step={step}
            outputId={`${condition.output_id}${REPEAT_LATEST_OUTPUT_SUFFIX}`}
          />
          <Text size="xs">Latest</Text>
        </Group>
        <Group gap={6} wrap={'nowrap'}>
          <OutputPinButton step={step} outputId={`${condition.output_id}${REPEAT_ALL_OUTPUT_SUFFIX}`} />
          <Text size="xs">All</Text>
        </Group>
      </Group>
    </Stack>
  );
};

export default RepeatDataInputFields;
